import type { SchuelerSchulbesuchMerkmal } from "@core/asd/data/schueler/SchuelerSchulbesuchMerkmal";
import type { SchuelerSchulbesuchSchule } from "@core/asd/data/schueler/SchuelerSchulbesuchSchule";
import type { SchuelerSchulbesuchsdaten } from "@core/asd/data/schueler/SchuelerSchulbesuchsdaten";
import type { Schuljahresabschnitt } from "@core/asd/data/schule/Schuljahresabschnitt";

export interface SchulbesuchLookups {
	abschnitteById: Map<number, Schuljahresabschnitt>;
}

export class SchuelerSchulbesuchManager {

	private readonly _daten: SchuelerSchulbesuchsdaten;
	private readonly _idSchueler: number;
	private readonly _schuljahr: number;
	private readonly _idSchuljahresabschnitt: number;

	private readonly _schuljahresabschnitteById: Map<number, Schuljahresabschnitt> = new Map();

	public constructor(
		schulbesuchsdaten: SchuelerSchulbesuchsdaten,
		idSchueler: number,
		idSchuljahresabschnitt: number,
		lookups: SchulbesuchLookups) {
		this._daten = schulbesuchsdaten;
		this._idSchueler = idSchueler;
		this._idSchuljahresabschnitt = idSchuljahresabschnitt;
		this._schuljahresabschnitteById = lookups.abschnitteById;
		this._schuljahr = this.getSchuljahr();
	}

	private getSchuljahr(): number {
		return this._schuljahresabschnitteById.get(this._idSchuljahresabschnitt)?.schuljahr ?? -1;
	}

	// --- Merkmale ---

	public addMerkmal(m: SchuelerSchulbesuchMerkmal) {
		this.daten.merkmale.add(m);
	}

	public patchMerkmalById(id: number, data: Partial<SchuelerSchulbesuchMerkmal>) {
		const merkmal = this.getMerkmalById(id);
		if (merkmal === undefined) {
			return;
		}
		Object.assign(merkmal, data);
	}

	private getMerkmalById(id: number): SchuelerSchulbesuchMerkmal | undefined {
		for (const merkmal of this.daten.merkmale) {
			if (merkmal.id === id) {
				return merkmal;
			}
		}
		return undefined;
	}

	public deleteMerkmal(id: number) {
		for (let i = 0; i < this.daten.merkmale.size(); i++) {
			if (this.daten.merkmale.get(i).id === id) {
				this.daten.merkmale.removeElementAt(i);
			}
		}
	}

	// --- Bisherige Schulen ---

	public addBisherigeSchule(s: SchuelerSchulbesuchSchule) {
		this.daten.bisherBesuchteSchulen.add(s);
	}

	public patchBisherigeSchuleById(id: number, data: Partial<SchuelerSchulbesuchSchule>) {
		const schule = this.getBisherigeSchuleById(id);
		if (schule === undefined) {
			return;
		}
		Object.assign(schule, data);
	}

	public deleteBisherigeSchuleById(id: number) {
		for (let i = 0; i < this.daten.bisherBesuchteSchulen.size(); i++) {
			if (this.daten.bisherBesuchteSchulen.get(i).id === id) {
				this.daten.bisherBesuchteSchulen.removeElementAt(i);
			}
		}
	}

	private getBisherigeSchuleById(id: number): SchuelerSchulbesuchSchule | undefined {
		for (const schule of this.daten.bisherBesuchteSchulen) {
			if (schule.id === id) {
				return schule;
			}
		}
		return undefined;
	}

	get daten(): SchuelerSchulbesuchsdaten {
		return this._daten;
	}

	get idSchueler(): number {
		return this._idSchueler;
	}

	get schuljahr(): number {
		return this._schuljahr;
	}

	get idSchuljahresabschnitt(): number {
		return this._idSchuljahresabschnitt;
	}

	get schuljahresabschnitteById(): Map<number, Schuljahresabschnitt> {
		return this._schuljahresabschnitteById;
	}

}
